import type {
	MonitoringConfigValues,
	MonitoringThresholdKey,
	MonitoringThresholds,
} from "../types/types";

export type ThresholdFieldKind = "share" | "decimal" | "integer" | "stattest";

export interface ThresholdField {
	key: MonitoringThresholdKey;
	kind: ThresholdFieldKind;
	/** For `stattest` fields — the tests Evidently offers for that column type. */
	options?: readonly string[];
}

export interface ThresholdGroup {
	id: "drift" | "stattests" | "quality" | "volume";
	fields: ThresholdField[];
}

// Evidently's stat tests per column type, most used first.
export const NUMERIC_STATTESTS = [
	"ks",
	"psi",
	"wasserstein",
	"jensenshannon",
	"kl_div",
	"anderson",
	"cramer_von_mises",
	"ed",
	"t_test",
] as const;

export const CATEGORICAL_STATTESTS = [
	"psi",
	"jensenshannon",
	"chisquare",
	"z",
	"kl_div",
	"fisher_exact",
	"g_test",
	"TVD",
] as const;

/** The 18 tunable thresholds, in the order the form presents them. */
export const THRESHOLD_GROUPS: ThresholdGroup[] = [
	{
		id: "drift",
		fields: [
			{ key: "drift_share_warning", kind: "share" },
			{ key: "drift_share_critical", kind: "share" },
			{ key: "max_drifted_importance", kind: "share" },
			{ key: "multivariate_drift_threshold", kind: "share" },
		],
	},
	{
		id: "stattests",
		fields: [
			{ key: "num_stattest", kind: "stattest", options: NUMERIC_STATTESTS },
			{ key: "num_stattest_threshold", kind: "decimal" },
			{ key: "cat_stattest", kind: "stattest", options: CATEGORICAL_STATTESTS },
			{ key: "cat_stattest_threshold", kind: "decimal" },
		],
	},
	{
		id: "quality",
		fields: [
			{ key: "min_roc_auc", kind: "share" },
			{ key: "min_gini", kind: "share" },
			{ key: "max_auc_drop", kind: "share" },
			{ key: "max_ece", kind: "share" },
			{ key: "min_r2", kind: "decimal" },
			{ key: "min_estimated_roc_auc", kind: "share" },
		],
	},
	{
		id: "volume",
		fields: [
			{ key: "min_rows_for_verdict", kind: "integer" },
			{ key: "min_rows_for_quality_tests", kind: "integer" },
			{ key: "min_rows_for_model_quality", kind: "integer" },
			{ key: "max_analysis_rows", kind: "integer" },
		],
	},
];

export const THRESHOLD_FIELDS: ThresholdField[] = THRESHOLD_GROUPS.flatMap(
	(group) => group.fields,
);

/** Form state: every field as typed text, "" meaning "use the global value". */
export type ThresholdDraft = Record<MonitoringThresholdKey, string>;

export const emptyThresholdDraft = (): ThresholdDraft =>
	Object.fromEntries(
		THRESHOLD_FIELDS.map((field) => [field.key, ""]),
	) as ThresholdDraft;

/** Fills the draft from a model's own overrides; unset ones stay empty. */
export const draftFromThresholds = (
	thresholds: MonitoringThresholds | null | undefined,
): ThresholdDraft => {
	const draft = emptyThresholdDraft();
	for (const field of THRESHOLD_FIELDS) {
		const value = thresholds?.[field.key];
		if (value !== null && value !== undefined) draft[field.key] = String(value);
	}
	return draft;
};

const parseNumber = (raw: string): number | null => {
	const normalized = raw.trim().replace(",", ".");
	if (normalized === "") return null;
	const value = Number(normalized);
	return Number.isFinite(value) ? value : null;
};

export type ThresholdError = "number" | "integer" | "share" | "order";

/** Per-field validation; empty fields are always valid (they inherit). */
export const validateThresholdDraft = (
	draft: ThresholdDraft,
	effective: MonitoringConfigValues,
): Partial<Record<MonitoringThresholdKey, ThresholdError>> => {
	const errors: Partial<Record<MonitoringThresholdKey, ThresholdError>> = {};

	for (const field of THRESHOLD_FIELDS) {
		const raw = draft[field.key].trim();
		if (raw === "" || field.kind === "stattest") continue;
		const value = parseNumber(raw);
		if (value === null) {
			errors[field.key] = "number";
		} else if (
			field.kind === "integer" &&
			(!Number.isInteger(value) || value < 0)
		) {
			errors[field.key] = "integer";
		} else if (field.kind === "share" && (value < 0 || value > 1)) {
			errors[field.key] = "share";
		}
	}

	// The service judges warning before critical, so the order must hold for
	// whichever pair ends up in force.
	if (!errors.drift_share_warning && !errors.drift_share_critical) {
		const warning =
			parseNumber(draft.drift_share_warning) ??
			toNumber(effective.drift_share_warning);
		const critical =
			parseNumber(draft.drift_share_critical) ??
			toNumber(effective.drift_share_critical);
		if (warning !== null && critical !== null && critical <= warning) {
			errors.drift_share_critical = "order";
		}
	}

	return errors;
};

export const toNumber = (value: unknown): number | null =>
	typeof value === "number" && Number.isFinite(value) ? value : null;

/**
 * Only what the user actually changed against the model's current overrides.
 * Clearing a field is not sendable — the API merges, it cannot unset — so an
 * emptied field is left out rather than reset.
 */
export const thresholdPayload = (
	draft: ThresholdDraft,
	current: MonitoringThresholds | null | undefined,
): MonitoringThresholds => {
	const payload: Record<string, number | string> = {};

	for (const field of THRESHOLD_FIELDS) {
		const raw = draft[field.key].trim();
		if (raw === "") continue;
		const value = field.kind === "stattest" ? raw : parseNumber(raw);
		if (value === null) continue;
		if (current?.[field.key] === value) continue;
		payload[field.key] = value;
	}

	return payload as MonitoringThresholds;
};
